import React, { useEffect, useMemo } from "react"

import EventEmitter from "src/EventEmitter"

import { gql, useClient, useMutation, useQuery } from "hooks/urql"
import useWebsocket from "hooks/useWebsocket"

import NotificationContainer from "components/NotificationContainer"
import UrqlLoadingIndicator from "components/WithLoadingIndicator/urql"

import AddUser from "./AddUser"
import AvatarGroup from "./AvatarGroup"
import Message from "./Message"
import SendMessage from "./SendMessage"

export type MessageThreadDetailsProps = {
  id: string
  userId: string
}

const MessageThreadDetails: React.FC<MessageThreadDetailsProps> = ({
  id,
  userId,
}) => {
  const client = useClient()
  const [result, refetch] = useQuery({
    query: messageThreadQuery,
    variables: { id },
    client,
    requestPolicy: "cache-and-network",
  })

  const [, runMutation]: any[] = useMutation(markReadMutation, client)

  const markRead = () => {
    setTimeout(
      () =>
        runMutation({ messageThreadId: id }).then(() => {
          refetch()
        }),
      2000
    )
  }

  const channelProps = useMemo(() => {
    return { id: userId }
  }, [userId])

  useWebsocket({
    channelName: "MessageUserChannel",
    channelProps,
  })

  useEffect(() => {
    markRead()
    const handleEvent = () => {
      refetch()
      markRead()
    }
    EventEmitter.subscribe("messageReceived", handleEvent)

    return () => {
      EventEmitter.unsubscribe("messageReceived", handleEvent)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const studentCount = thread => {
    return thread.users.filter(user => user.type === "Student").length
  }

  return (
    <>
      <UrqlLoadingIndicator result={result}>
        {({ data: { viewer } }) => (
          <>
            <NotificationContainer />
            <h1>{viewer.messageThread.title}</h1>
            <div className="mb-4 flex items-start">
              <AvatarGroup
                messageThreadId={id}
                users={viewer.messageThread.users}
                canDelete={viewer.id === viewer.messageThread.author.id}
                viewerId={viewer.id}
              />
              {viewer.id === viewer.messageThread.author.id && (
                <div className="ml-3">
                  <AddUser messageThreadId={id} viewerType={viewer.type} />
                </div>
              )}
            </div>

            <div>
              <h2>Messages</h2>
              <SendMessage threadId={id} />
            </div>

            {studentCount(viewer.messageThread) > 1 && (
              <>
                {viewer.type === "Tutor" && (
                  <div className="mb-6 rounded-md bg-sky-100 px-4 py-2 font-medium text-sky-600">
                    Students can see your messages, but they cannot see other
                    students' replies. Report any inappropriate student messages
                    to your admin slack channel.
                  </div>
                )}
                {viewer.type === "Student" && (
                  <div className="mb-6 rounded-md bg-sky-100 px-4 py-2 font-medium text-sky-600">
                    Other students will not be able to see your messages.
                  </div>
                )}
              </>
            )}
            {viewer.messageThread.messages.data.map(message => (
              <Message key={message.id} message={message} viewer={viewer} />
            ))}
            {viewer.messageThread.messagesCount >
              viewer.messageThread.messages.data.length && (
              <a
                href={viewer.messageThread.archivePath}
                className="btn blue outline"
              >
                View All Messages
              </a>
            )}
          </>
        )}
      </UrqlLoadingIndicator>
    </>
  )
}

const messageThreadQuery = gql`
  query MessageThread($id: ID!) {
    viewer {
      id
      type
      messageThread(id: $id) {
        id
        title
        messagesCount
        archivePath
        users {
          id
          type
          messagingDisplayName
          avatar {
            thumbUrl
          }
        }
        author {
          id
        }
        messages {
          data {
            id
            read
            content
            contentFlagged
            createdAt
            author {
              id
              type
              messagingDisplayName
              avatar {
                thumbUrl
              }
            }
            attachment {
              filename
              contentType
              downloadUrl
              url
              thumbUrl
              previewUrl
            }
          }
        }
      }
    }
  }
`

const markReadMutation = gql`
  mutation MarkRead($messageThreadId: ID!) {
    messageThreadMarkRead(messageThreadId: $messageThreadId) {
      failures {
        message
      }
    }
  }
`

export default MessageThreadDetails
